import {
  DEFAULT_ENTERPRISE_INVOICE_PRODUCT_NAMES,
  ENTERPRISE_INVOICE_PRODUCT_NAMES,
  normalizeProductNames
} from './productNames.js'

export function defaultProductQuotas(targetAmount = 0) {
  return [{
    productNames: [...DEFAULT_ENTERPRISE_INVOICE_PRODUCT_NAMES],
    displayAmount: normalizeQuotaAmount(targetAmount)
  }]
}

export function normalizeProductQuotas(value, targetAmount = 0) {
  const quotas = Array.isArray(value) ? value : []
  const normalized = []
  const usedNames = new Set()

  for (const quota of quotas) {
    const names = normalizeQuotaProductNames(quota?.productNames || quota?.productName)
      .filter(name => !usedNames.has(name))
    const displayAmount = normalizeQuotaAmount(quota?.displayAmount)
    if (names.length === 0 || displayAmount <= 0) continue
    for (const name of names) usedNames.add(name)
    normalized.push({ productNames: names, displayAmount })
  }

  if (normalized.length > 0) return normalized
  return defaultProductQuotas(targetAmount)
}

export function normalizeProductQuotaDrafts(value, targetAmount = 0) {
  const quotas = Array.isArray(value) ? value : []
  const normalized = []
  const usedNames = new Set()

  for (const quota of quotas) {
    const names = normalizeQuotaProductNames(quota?.productNames || quota?.productName)
      .filter(name => !usedNames.has(name))
    if (names.length === 0) continue
    for (const name of names) usedNames.add(name)
    normalized.push({
      productNames: names,
      displayAmount: normalizeQuotaAmount(quota?.displayAmount)
    })
  }

  if (normalized.length > 0) return normalized
  return defaultProductQuotas(targetAmount)
}

export function normalizeQuotaProductNames(value) {
  const source = Array.isArray(value)
    ? value
    : String(value || '').split(/[、,，/]/)
  return normalizeProductNames(source)
}

export function normalizeQuotaAmount(value) {
  if (value === null || value === undefined || value === '') return 0
  const amount = Number(value)
  if (!Number.isFinite(amount)) return 0
  return Math.round(amount / 100) * 100
}

export function quotaTotalAmount(quotas = []) {
  return (quotas || []).reduce((total, quota) => total + normalizeQuotaAmount(quota?.displayAmount), 0)
}

export function quotaTotalMatched(quotas = [], targetAmount = 0) {
  return quotaTotalAmount(quotas) === normalizeQuotaAmount(targetAmount)
}

export function hasSameProductQuotas(left, right) {
  const leftQuotas = normalizeProductQuotas(left, 0)
  const rightQuotas = normalizeProductQuotas(right, 0)
  if (leftQuotas.length !== rightQuotas.length) return false
  return leftQuotas.every((quota, index) => {
    const other = rightQuotas[index]
    return quota.displayAmount === other.displayAmount &&
      sameProductNameSet(quota.productNames, other.productNames)
  })
}

export function hasSameProductQuotaDrafts(left, right) {
  const leftQuotas = normalizeProductQuotaDrafts(left, 0)
  const rightQuotas = normalizeProductQuotaDrafts(right, 0)
  if (leftQuotas.length !== rightQuotas.length) return false
  return leftQuotas.every((quota, index) => {
    const other = rightQuotas[index]
    return quota.displayAmount === other.displayAmount &&
      sameProductNameSet(quota.productNames, other.productNames)
  })
}

export function validateProductQuotaList(quotas, targetAmount, amountStep = 100) {
  const errors = []
  const rows = Array.isArray(quotas) ? quotas : []
  const usedNames = new Set()
  if (rows.length === 0) errors.push('至少保留一条品名组')
  rows.forEach((quota, index) => {
    const rowNo = index + 1
    const names = Array.isArray(quota?.productNames) ? quota.productNames : []
    if (names.length === 0) errors.push(`第 ${rowNo} 行至少选择一个品名`)
    for (const name of names) {
      if (!ENTERPRISE_INVOICE_PRODUCT_NAMES.includes(name)) {
        errors.push(`第 ${rowNo} 行包含无效品名`)
      }
      if (usedNames.has(name)) {
        errors.push(`品名“${name}”不能在同一方向重复使用`)
      }
      usedNames.add(name)
    }
    const amount = normalizeQuotaAmount(quota?.displayAmount)
    if (amount <= 0) errors.push(`第 ${rowNo} 行金额必须大于 0`)
    if (amountStep > 0 && amount % amountStep !== 0) errors.push(`第 ${rowNo} 行金额必须符合步长`)
  })
  if (!quotaTotalMatched(rows, targetAmount)) {
    errors.push('金额合计需等于目标额度')
  }
  return errors
}

export function formatProductQuotaSummary(quotas = [], { maxItems = 2 } = {}) {
  const normalized = normalizeProductQuotas(quotas, 0)
  const parts = normalized.map(quota =>
    `${quota.productNames.join('/')} ${formatWanAmount(quota.displayAmount)}`
  )
  const safeMax = Math.max(1, Number(maxItems) || 2)
  const shown = parts.slice(0, safeMax)
  const hiddenCount = Math.max(0, parts.length - safeMax)
  return {
    shortText: shown.length === 0 ? '-' : `${shown.join('、')}${hiddenCount > 0 ? ` +${hiddenCount}` : ''}`,
    fullText: parts.length === 0 ? '-' : parts.join('、')
  }
}

function sameProductNameSet(left = [], right = []) {
  const leftNames = [...new Set(left)].sort()
  const rightNames = [...new Set(right)].sort()
  return leftNames.length === rightNames.length &&
    leftNames.every((name, index) => name === rightNames[index])
}

function formatWanAmount(value) {
  return `${Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: 2
  }).format(Number(value || 0) / 10000)} 万`
}
